import { Component, DestroyRef, inject, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbOffcanvas, NgbTooltip, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem } from '@ng-bootstrap/ng-bootstrap';
import { PlanogramService } from '../services/planogram.service';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { PlanogramStatusType, PlanogramStatus } from '../models/planogram-status';
import { ProductOrientation } from "../models/layout/product-orientation";
import { ModalService } from '../services/modal.service';
import { ExportPlanogramModalComponent } from '../modals/export-planogram-modal/export-planogram-modal.component';
import { CombinedPlanogramsPdfModalComponent } from '../modals/combined-planograms-pdf-modal/combined-planograms-pdf-modal.component';
import { ProductOverrideService } from 'src/app/services/product-override.service';
import { ToastService } from '../services/toasts.service';
import { SaveModalComponent } from '../modals/save-modal/save-modal.component';
import { LoadModalComponent } from '../modals/load-modal/load-modal.component';
import { ZoomService } from '../services/zoom.service';
import { SelectReportModalComponent } from '../modals/select-report-modal/select-report-modal.component';
import { PlanogramApiService } from '../services/planogram-api.service';
import { SignalRService } from '../services/signalr.service';
import { firstValueFrom, Subject } from 'rxjs';
import { LayoutService } from '../services/layout.service';
import { ImportModalComponent } from '../modals/import-modal/import-modal.component';
import { VisualAnalysisService } from '../services/visualanalysis.service';
import { ImagesManagerComponent } from '../modals/images-manager/images-manager.component';
import { AuthService } from '../services/auth.service';
import { NotesEditorModalComponent } from '../modals/notes-editor-modal/notes-editor-modal.component';
import { RemoveProductsModalComponent } from '../modals/remove-products-modal/remove-products-modal.component';
import { LogosManagerComponent } from '../modals/logos-manager/logos-manager.component';
import { ProblemsModalComponent } from '../modals/problems-modal/problems-modal.component';
import { PlanogramProblemsService } from '../services/planogram-problems.service';
import { SystemRole } from '../models/roles';
import { RolesService } from '../services/roles.service';
import { CapabilityType } from '../models/capability-type';
import { SharePlanogramModalComponent } from '../modals/share-planogram-modal/share-planogram-modal.component';
import { PlanogramFeedbackModalComponent } from '../modals/planogram-feedback-modal/planogram-feedback-modal.component';
import { DataBreakdownComponent } from '../components/planogram/data-breakdown/data-breakdown.component';
import { PlanogramOptionsComponent } from '../components/planogram/planogram-options/planogram-options.component';
import { Router } from '@angular/router';
import { EventsService } from '../services/events.service';
import { EventType } from '../models/events';
import { ShareService } from '../services/share.service';
import { DialogService } from '../services/dialog.service';
import { CapabilitiesService } from '../services/capabilities.service';
import { RefreshPlanogramModalComponent } from '../modals/refresh-planogram-modal/refresh-planogram-modal.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { HasCapabilityDirective } from '../directives/has-capability.directive';
import { FormsModule } from '@angular/forms';
import { NotificationsComponent } from '../components/notifications/notifications.component';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [NgIf, HasCapabilityDirective, NgbTooltip, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, FormsModule, NotificationsComponent, AsyncPipe]
})
export class HeaderComponent {
  CapabilityType = CapabilityType;
  SystemRole = SystemRole;
  ZoomService = ZoomService;
  /** True when the open planogram is a linked (reference) planogram — shown as a view-only badge. */
  get isLinkedPlanogram(): boolean { return this.planogramService.isLinkedPlanogram(); }
  /** True on the home / folder-browsing pages (both served by HomeComponent) — gates the Tools menu. */
  get isHomePage(): boolean {
    const url = this.router.url.split('?')[0];
    return url === '/' || url.startsWith('/folder');
  }
  /** External (shared) users must never see internal tools. Gates the Tools menu alongside isHomePage. */
  get isExternalUser(): boolean {
    return this.rolesService.roles.value.includes(SystemRole.EXTERNAL);
  }
  public currentStatus: PlanogramStatus | null = null;
  public planogramZoom: number = 4;
  private zoomSubject = new Subject<number>();
  canEnableManualMode: boolean = false;

  isDataBreakdownFullscreen: boolean = false;
  @ViewChild('dataBreakdown') dataBreakdown!: TemplateRef<any>;

  private destroyRef = inject(DestroyRef);

  constructor(
    public authService: AuthService,
    public planogramProblemsService: PlanogramProblemsService, 
    public visualAnalysisService: VisualAnalysisService,
    public rolesService: RolesService,
    private zoomService: ZoomService,
    private capabilitiesService: CapabilitiesService,
    private planogramApiService: PlanogramApiService,
    private signalRService: SignalRService,
    private layoutService: LayoutService,
    private planogramService: PlanogramService,
    private modalService: ModalService,
    private offcanvasService: NgbOffcanvas,
    private productOverrideService: ProductOverrideService,
    private eventsService: EventsService,
    private toastService: ToastService,
    private shareService: ShareService,
    private dialogService: DialogService,
    private router: Router) { }

  ngOnInit() {
    this.planogramService.status$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status: PlanogramStatus) => {
      this.currentStatus = status;
    });

    this.zoomService.zoom$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(zoom => {
      this.planogramZoom = zoom;
    });

    this.zoomSubject.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.zoomService.setZoom(value);
    });
  }

  canUndo(): boolean {
    return this.layoutService.canUndo();
  }

  canRedo(): boolean {
    return this.layoutService.canRedo();
  }

  undo() {
    this.layoutService.undo();
  }

  redo() {
    this.layoutService.redo();
  }

  logout() {
    this.rolesService.resetRoles();
    this.authService.logout();
  }

  toggleAnalysisOverlay(){
    this.visualAnalysisService.toggle();
  }

  setZoom(value: any) {
    this.zoomSubject.next(value);
  }

  zoomIn() {
    this.zoomService.incrementZoom();
  }

  zoomOut() {
    this.zoomService.decrementZoom();
  }

  getCurrentStatus() {
    return this.currentStatus != null ? PlanogramStatusType[this.currentStatus.state] : "";
  }

  saveDefaultProductOrientations() {
    let productOrientations = new Map<number, ProductOrientation>();
    this.productOverrideService.getOverrides().map((override) => {
      if (override.orientation != null) {
        productOrientations.set(override.productId, override.orientation);
      }
    });

    this.planogramApiService.updateMerchDefaultOrientations(
      this.planogramService.getMerchId(),
      productOrientations
    ).subscribe(() => {
      this.toastService.show('Updated default orientations', {
        classname: `bg-dark text-light p-3 fs-5`,
        delay: 3000
      });
    });
  }

  goHome() {
    this.router.navigate(['/']);
  }

  gotoActivityCenter(){
    this.router.navigate(['/sharing-center']);
  }

  openSharePlanogramModal(){
    let stateId = this.planogramService.getPlanogramState();

    if(!stateId){
      this.dialogService.showWarning(
        'Please save the planogram before attempting to share.',
        'Save Planogram First'
      );
      return;
    }

    this.modalService.openModalWithOptions(SharePlanogramModalComponent, {
      centered: true,
      size: 'xl'
    });
  }

  openPlanogramFeedbackModal(){
    this.modalService.openModalWithOptions(PlanogramFeedbackModalComponent, {
      centered: true,
      size: 'xl'
    });
  }

  openProblemsModal(){
    this.modalService.openModalWithOptions(ProblemsModalComponent, {
      centered: true,
      size: 'xl'
    });
  }

  openNotesModal(){
    this.modalService.openModalWithOptions(NotesEditorModalComponent, {
      centered: true,
      size: 'xl'
    });
  }
  
  openImagesManager(){
    this.modalService.openModalWithOptions(ImagesManagerComponent, {
      centered: true,
      size: 'xl'
    });
  }
  
  openLogosManager(){
    this.modalService.openModalWithOptions(LogosManagerComponent, {
      centered: true,
      size: 'xl'
    });
  }

  openSelectReportModal() {
    this.modalService.openModalWithOptions(SelectReportModalComponent, {
      centered: true
    });
  }

  openPlanogramRefreshModal() {
    this.modalService.openModalWithOptions(RefreshPlanogramModalComponent, {
      size: 'xl',
      centered: true
    });
  }

  openExportPlanogramModal() {
    this.modalService.openModalWithOptions(ExportPlanogramModalComponent, {
      centered: true
    });
  }

  /** Opens the Combined Planograms PDF tool (stitch several planograms of a store into one PDF). */
  openCombinedPlanogramsPdfModal() {
    this.modalService.openModalWithOptions(CombinedPlanogramsPdfModalComponent, {
      centered: true,
      size: 'lg'
    });
  }

  openRemoveProductsModal(){
    this.modalService.openModalWithOptions(RemoveProductsModalComponent, {
      size: 'xl',
      centered: true
    });
  }

  openImportPlanogramModal(){
    if(this.planogramService.getPlanogramState() == null)
    {
      this.toastService.show(`Please save planogram before attempting to import products or refresh product data.`, { classname: `bg-dark text-danger p-3 fs-5`, delay: 3000 });
      return;
    }
    this.modalService.openModalWithOptions(ImportModalComponent, {
      centered: true
    });
  }

  openPlanogramSaveModal() {
    this.modalService.openModalWithOptions(SaveModalComponent, {
      centered: true
    });
  }

  async openPlanogramLoadModal() {
    const stateList = await firstValueFrom(this.planogramApiService.getPlanogramStates());
    if (stateList) {
      await this.modalService.openModalWithOptions(LoadModalComponent, {
        size: 'xl',
        centered: true
      }, {
        states: stateList.states
      });
    }
  }

  async savePlanogram() {
    const planogramId = this.planogramService.getPlanogram();
    if(!planogramId)
      return;

    const stateId = this.planogramService.getPlanogramState();
    if(stateId == null){
      this.openPlanogramSaveModal();
      return;
    }

    if (this.rolesService.roles.value.includes(SystemRole.EXTERNAL)) {
      await this.eventsService.createEvent({
        planogramId: planogramId,
        eventType: EventType.PLANOGRAM_SAVED,
        description: 'Used saved the planogram state',
        metadata: stateId
      });
    }

    this.planogramApiService.savePlanogram(stateId, null, false)
      .pipe(take(1))
      .subscribe({
        next: (stateId: string | null) => {
          if (stateId) {
            this.planogramService.setPlanogramState(stateId);
            this.layoutService.clearHistory();
          }
          else {
            this.toastService.show('Failed to save planogram', {
              classname: `bg-danger text-light p-3 fs-5`,
              delay: 3000
            });
          }
        }
      });
  }

  generatePlanogram() {
    if(!this.capabilitiesService.hasCapability(CapabilityType.GENERATE_PLANOGRAM)){
      return;
    }
    this.signalRService.generatePlanogram();
  }

  resetPlanogram() {
    const planogramId = this.planogramService.getPlanogram();
    if (!planogramId) return;

    this.dialogService
      .showConfirmation(
        'Reset Planogram',
        'Are you sure you want to reset this planogram to its original state? This action cannot be undone.'
      )
      .then((confirmed) => {
        if (!confirmed) return;

        this.dialogService.showLoading('Resetting planogram...');
        this.shareService.resetPlanogramState(planogramId).subscribe(async (state) => {
          this.dialogService.closeLoading();
          if (state) {
            this.planogramService.setPlanogramState(state);
            await this.signalRService.loadPlanogramState(state);
            this.dialogService.showSuccess(
              'The planogram has been reset back to its original state.',
              'Planogram Reset'
            );
          } else {
            this.dialogService.showError(
              'An unexpected error occurred and the planogram was unable to be reset.',
              'Reset planogram failed'
            );
          }
        });
      });
  }

  private activeOffcanvasComponent: any = null;

  openPlanogramOptions() {
    this.switchOffcanvas(
      PlanogramOptionsComponent,
      { position: 'end', backdrop: true, ariaLabelledBy: 'offcanvas-settings-title' }
    );
  }

  openDataBreakdown() {
    this.switchOffcanvas(
      DataBreakdownComponent,
      { position: 'bottom', backdrop: false }
    );
  }

  private switchOffcanvas(component: any, options: any) {
    const sameComponentOpen =
      this.activeOffcanvasComponent === component &&
      this.offcanvasService.hasOpenOffcanvas();

    if (this.visualAnalysisService.isOpen) {
      this.visualAnalysisService.setOpen(false);
    }
    const hadOpen = this.offcanvasService.hasOpenOffcanvas();
    if (hadOpen) {
      this.offcanvasService.dismiss();
    }
    if (sameComponentOpen) {
      this.activeOffcanvasComponent = null;
      return;
    }
    const openIt = () => {
      const ref = this.offcanvasService.open(component, options);
      this.activeOffcanvasComponent = component;
      ref.dismissed.subscribe(() => {
        if (this.activeOffcanvasComponent === component) {
          this.activeOffcanvasComponent = null;
        }
      });
      ref.closed.subscribe(() => {
        if (this.activeOffcanvasComponent === component) {
          this.activeOffcanvasComponent = null;
        }
      });
    };
    if (hadOpen) {
      Promise.resolve().then(openIt);
    } else {
      openIt();
    }
  }

  canSavePlanogram(): boolean {
    return this.planogramService.getPlanogramState() != null;
  }

  isLayoutVisible(): boolean {
    if (this.currentStatus == null) { return false; }
    return this.currentStatus.state == PlanogramStatusType.READY;
  }

  canGeneratePlanogram(): boolean {
    return this.capabilitiesService.hasCapability(CapabilityType.GENERATE_PLANOGRAM);
  }

  isGenerateButtonVisible(): boolean {
    if (this.currentStatus == null) { return false; }
    if(this.currentStatus.state == PlanogramStatusType.READY)
      return true;
    return false;
  }

  isPlanogramOptionsVisible(): boolean {
    if (this.currentStatus == null) { return false; }
    if (this.currentStatus.state == PlanogramStatusType.READY) {
      return false;
    }
    return true;
  }
}
